import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip,
  IconButton, Button, AppBar, Toolbar, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Stack
} from '@mui/material';
import {
  Store as StoreIcon,
  ExitToApp as LogoutIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

function PlatformApplications() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const token = localStorage.getItem('platform_token');
  const admin = JSON.parse(localStorage.getItem('platform_admin') || '{}');

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/platform'); return; }
    fetchApplications();
  }, [token, page, rowsPerPage, statusFilter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(statusFilter && { status: statusFilter })
      };
      const res = await api.get('/platform/applications', { headers, params });
      setApplications(res.data.data.items || []);
      setTotal(res.data.data.pagination?.total || 0);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('platform_token');
        navigate('/platform');
      } else {
        setError(t('platform.applications.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('platform_token');
    localStorage.removeItem('platform_admin');
    navigate('/platform');
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('zh-TW', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <StoreIcon sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>{t('platform.nav.title')}</Typography>
          <Button color="inherit" component={Link} to="/platform/dashboard" startIcon={<DashboardIcon />}>
            {t('platform.nav.dashboard')}
          </Button>
          <Button color="inherit" component={Link} to="/platform/tenants" startIcon={<PeopleIcon />}>
            {t('platform.nav.tenants')}
          </Button>
          <Typography sx={{ mx: 2 }}>{admin.name || admin.username}</Typography>
          <IconButton color="inherit" onClick={fetchApplications}><RefreshIcon /></IconButton>
          <IconButton color="inherit" onClick={handleLogout}><LogoutIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>{t('platform.applications.title')}</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Filter */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('platform.applications.statusFilter')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('platform.applications.statusFilter')}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">{t('platform.common.all')}</MenuItem>
              <MenuItem value="pending">{t('platform.status.pending')}</MenuItem>
              <MenuItem value="provisioning">{t('platform.status.provisioning')}</MenuItem>
              <MenuItem value="completed">{t('platform.status.completed')}</MenuItem>
              <MenuItem value="rejected">{t('platform.status.rejected')}</MenuItem>
              <MenuItem value="failed">{t('platform.status.failed')}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('platform.applications.totalCount', { count: total })}
          </Typography>
        </Paper>

        {/* Applications Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>ID</TableCell>
                  <TableCell>{t('platform.applications.storeName')}</TableCell>
                  <TableCell>{t('platform.applications.applicant')}</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>{t('platform.tenants.plan')}</TableCell>
                  <TableCell align="center">{t('platform.tenants.status')}</TableCell>
                  <TableCell>{t('platform.applications.applyTime')}</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : applications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">{t('platform.applications.noData')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  applications.map((app) => {
                    const st = { label: t(`platform.status.${app.status}`, { defaultValue: app.status }), color: { pending: 'warning', provisioning: 'info', completed: 'success', rejected: 'error', failed: 'error' }[app.status] || 'default' };
                    return (
                      <TableRow key={app.id} hover>
                        <TableCell>{app.id}</TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">{app.platform_name || app.store_name || '-'}</Typography>
                        </TableCell>
                        <TableCell>{app.applicant_name || app.contact_name || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {app.applicant_email || app.contact_email || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(app.slug || app.subdomain) ? (
                            <Typography variant="body2" color="primary.main">
                              {(app.slug || app.subdomain)}.urstore.tw
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip label={app.plan_name || t('platform.common.free')} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={st.label} color={st.color} size="small" />
                        </TableCell>
                        <TableCell>{formatDate(app.created_at)}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {app.uuid && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => window.open(`/apply/status/${app.uuid}`, '_blank', 'noopener,noreferrer')}
                              >
                                檢視
                              </Button>
                            )}
                            {app.status === 'completed' && (app.slug || app.subdomain) && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => window.open(`https://${app.slug || app.subdomain}.urstore.tw/admin`, '_blank', 'noopener,noreferrer')}
                              >
                                進店
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={t('platform.common.rowsPerPage')}
          />
        </Paper>
      </Container>
    </Box>
  );
}

export default PlatformApplications;
